import { CompactSettingsDropdown as CompactSettingsDropdownBase } from "@atlas-int/controls";
import type { SettingsGroup } from "@atlas-int/controls";
import { useCurrentVizType, useIsFeasibilityTable } from "../../../routing/typedRoutes";
import { useExploreParams } from "../../../visualization/paramSchemas";
import { UIView, VizType } from "@atlas-int/data";
import {
  getSettingsForViz,
  type SettingsContext,
  type SettingOption,
} from "@atlas-int/data/schemas";
import { worldGroupDatum } from "@atlas-int/data";
import { googleAnalyticsCustomEvent } from "../../../sharedUtilities/analytics";
import { useDefaultSettingsAction } from "./utils";
import { RcaSlider as RcaSliderBase } from "@atlas-int/controls";
import { useProductspaceParams } from "../../../visualization/paramSchemas";

// ─── Hook: build radio groups for current viz type ──────────────────────────

function useSettingsGroups(): SettingsGroup[] {
  const vizType = useCurrentVizType();
  const [query, setQuery] = useExploreParams();
  const isFeasibilityTable = useIsFeasibilityTable();

  const {
    view: uiView,
    productClass,
    productLevel,
    colorBy,
    exporter,
    importer,
  } = query;

  const isWorldExporter =
    exporter === worldGroupDatum.groupId || exporter === "";
  const isWorldImporter =
    importer === worldGroupDatum.groupId || importer === "";

  const ctx: SettingsContext = {
    vizType,
    uiView: uiView ?? UIView.Products,
    productClass,
    productLevel,
    colorBy,
    isWorldToWorld: isWorldExporter && isWorldImporter,
    isFeasibilityTable,
  };

  const settingsForViz = getSettingsForViz(vizType);

  return settingsForViz.flatMap((config): SettingsGroup[] => {
    // Check view visibility
    if (config.showInViews !== undefined) {
      if (typeof config.showInViews === "function") {
        if (!config.showInViews(ctx)) return [];
      } else {
        if (!config.showInViews.includes(ctx.uiView)) return [];
      }
    }

    // Resolve options (static array or context-dependent function)
    const rawOptions: SettingOption[] =
      typeof config.options === "function"
        ? config.options(ctx)
        : config.options;

    const isGroupDisabled = config.groupDisabledWhen?.(ctx) ?? false;

    // Apply per-option disable rules; when entire group is disabled, force all items disabled
    const items = rawOptions.map((opt) => ({
      label: opt.label,
      value: opt.value,
      disabled: isGroupDisabled || (opt.disabledWhen?.(ctx) ?? false),
    }));

    return [
      {
        label: config.label,
        tooltip:
          typeof config.tooltip === "function"
            ? config.tooltip(ctx)
            : config.tooltip,
        items,
        selectedValue: query[config.paramKey as keyof typeof query] as
          | string
          | number
          | undefined,
        disabled: isGroupDisabled,
        onChange: (value: string | number) => {
          if (isGroupDisabled) return;
          googleAnalyticsCustomEvent(
            "viz",
            "change",
            config.analyticsKey,
            "explore",
          );
          const sideEffects = config.sideEffects?.(value, ctx) ?? {};
          setQuery({ [config.paramKey]: value, ...sideEffects });
        },
      },
    ];
  });
}

// ─── Component ──────────────────────────────────────────────────────────────

interface CompactSettingsDropdownProps {
  onClose: () => void;
}

const RcaSlider = () => {
  const [{ rcaThreshold }, setQuery] = useProductspaceParams();

  return (
    <RcaSliderBase
      value={rcaThreshold ?? 1}
      onChange={(value) => setQuery({ rcaThreshold: value })}
      onChangeComplete={() =>
        googleAnalyticsCustomEvent("slider", "change", "rca", "explore")
      }
      tooltip="Filter products by their Revealed Comparative Advantage (RCA) value. An RCA > 1 means a country exports more than that product's share in total world trade."
    />
  );
};

const CompactSettingsDropdown = ({ onClose }: CompactSettingsDropdownProps) => {
  const vizType = useCurrentVizType();
  const groups = useSettingsGroups();
  const defaultSettingsAction = useDefaultSettingsAction(vizType);

  return (
    <CompactSettingsDropdownBase
      onClose={onClose}
      groups={groups}
      extraContent={vizType === VizType.ProductSpace ? <RcaSlider /> : undefined}
      onResetDefaults={defaultSettingsAction}
    />
  );
};

export default CompactSettingsDropdown;
